import { Link } from "react-router-dom";
import { ArrowRight, Compass } from "@phosphor-icons/react";
import ProductPageShell from "@/components/ProductPageShell";
import { DEMO_RELATIONSHIP_DATA } from "@/data/liveDemoData";
import { trackPublicEvent } from "@/lib/publicAnalytics";
import prepareNextCallEditorialDesktop from "@/assets/prepare-next-call-editorial-1600.webp";
import prepareNextCallEditorialMobile from "@/assets/prepare-next-call-editorial-1000.webp";

const source = "product_prepare_next_call";
function track(section, ctaId) {
  trackPublicEvent("cta_click", { source, section, cta_id: ctaId });
}

export default function ProductPrepareNextCallPage() {
  const prep = DEMO_RELATIONSHIP_DATA.relationship_memory?.prepare_next_call || {};
  const objections = Array.isArray(prep.likely_objections) ? prep.likely_objections.slice(0, 2) : [];
  const callFlow = Array.isArray(prep.suggested_call_flow) ? prep.suggested_call_flow.slice(0, 4) : [];
  const questions = Array.isArray(prep.questions_to_ask) ? prep.questions_to_ask.slice(0, 2) : [];

  return (
    <ProductPageShell source={source}>
      <section className="product-feature-hero product-feature-hero--prepare-next-call px-5 pb-16 pt-16 sm:px-8 sm:pb-24 sm:pt-28">
        <div className="product-prepare-next-call-art pointer-events-none absolute inset-0" aria-hidden="true">
          <picture className="product-prepare-next-call-art__picture">
            <source media="(max-width: 600px)" srcSet={prepareNextCallEditorialMobile} />
            <img
              className="product-prepare-next-call-art__image"
              src={prepareNextCallEditorialDesktop}
              alt=""
              decoding="async"
              fetchPriority="high"
            />
          </picture>
        </div>

        <div className="product-feature-hero__layout relative mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.94fr_1.06fr] lg:items-start lg:gap-16">
          <div className="product-feature-hero__copy">
            <h1 className="product-feature-hero__title landing-hero-title max-w-3xl text-[2.7rem] font-semibold leading-[1.01] tracking-[-0.055em] sm:text-6xl lg:text-7xl">
              <span className="product-feature-hero__title-base block">Walk into the next call</span>
              <span className="product-feature-hero__title-accent landing-hero-spectrum landing-hero-promise block">knowing what matters.</span>
            </h1>
            <p className="product-feature-hero-subcopy landing-hero-subcopy marketing-lead-copy mt-7 max-w-2xl">
              Callsect uses what happened across previous conversations — what changed, what they objected to, what was promised, and what still needs resolving — to prepare you for the next one.
            </p>
          </div>

          <article className="product-next-call-preview">
            <div className="product-next-call-preview__head">
              <span><Compass size={18} weight="duotone" /> Prepare Next Call</span>
              <strong>Walk in with a plan, not just a recap.</strong>
            </div>
            <div className="product-next-call-preview__strategy">
              <span>Plan</span>
              <strong>{prep.conversation_strategy}</strong>
            </div>
            <div className="product-next-call-preview__pair">
              <div>
                <span>Your goal</span>
                <strong>{prep.objective?.text}</strong>
              </div>
              <div>
                <span>Likely objection</span>
                <strong>{objections[0]?.text}</strong>
              </div>
            </div>
            <div className="product-next-call-preview__question">
              <span>Question to ask</span>
              <blockquote>“{questions[0]?.text}”</blockquote>
            </div>
          </article>
        </div>

        <div className="product-feature-hero__actions mx-auto flex flex-col gap-3 sm:flex-row">
          <Link to="/login?mode=register" onClick={() => track("hero", "sign_up_free")} className="product-feature-hero-primary landing-signup-primary-cta inline-flex items-center justify-center rounded-xl marketing-primary-action border px-5 py-3.5 font-medium transition">
            Sign up free
          </Link>
          <Link to="/demo" onClick={() => track("hero", "relationship_demo")} className="product-feature-hero-secondary landing-hero-secondary-cta inline-flex items-center justify-center gap-2 rounded-xl marketing-secondary-action border px-5 py-3.5 font-medium marketing-text-secondary-strong transition">
            See Demo
            <ArrowRight size={17} weight="bold" />
          </Link>
        </div>
      </section>

      <section className="product-feature-section px-5 py-16 sm:px-8 sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.76fr_1.24fr] lg:items-start lg:gap-16">
          <div className="product-feature-sticky product-feature-sticky--no-kicker">
            <h2>Prepare from the relationship, not just the last call.</h2>
          </div>

          <div className="product-next-call-detail">
            <section>
              <span>Likely objections</span>
              {objections.map((item) => (
                <div key={item.text}>
                  <strong>{item.text}</strong>
                  {item.response_direction ? <p>{item.response_direction}</p> : null}
                </div>
              ))}
            </section>
            <section>
              <span>Suggested call flow</span>
              <ol>
                {callFlow.map((item, index) => (
                  <li key={item.title}>
                    <b>{index + 1}</b>
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.purpose}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
            <section>
              <span>Questions to ask</span>
              {questions.map((item) => <blockquote key={item.text}>“{item.text}”</blockquote>)}
            </section>
          </div>
        </div>
      </section>

      <section className="product-feature-band px-5 py-14 sm:px-8 sm:py-16">
        <div className="marketing-highlight-band mx-auto flex max-w-6xl flex-col gap-6 rounded-2xl border p-7 sm:p-9 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <Compass size={25} weight="duotone" aria-hidden="true" />
            <h2>The next call starts with the relationship you already have.</h2>
          </div>
          <Link to="/product/relationship-memory" className="product-feature-inline-link">
            See relationship memory
            <ArrowRight size={17} weight="bold" />
          </Link>
        </div>
      </section>
    </ProductPageShell>
  );
}
